import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getRequest } from "../../service/index";
import { Calendar, Gift, Users } from "lucide-react";
import { Pagination } from "antd";

const EmployeeEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 2;

  useEffect(() => {
    const fetchEmployeeEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get today's date
        const today = new Date();
        const startDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        const endDate = startDate; // Same day for today's events
        
        const response = await getRequest(`/api/companies/employeeevents?startDate=${startDate}&endDate=${endDate}&type=all&page=1&limit=10`);
        const possibleEvents = response?.events ?? response?.data?.events ?? response?.data ?? [];
        setEvents(Array.isArray(possibleEvents) ? possibleEvents : []);
      } catch (err) {
        console.error('Error fetching employee events:', err);
        setError('Failed to load employee events');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeEvents();
  }, []);

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'birthday':
        return <Gift className="h-4 w-4 text-pink-500" />;
      case 'anniversary':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'joining':
        return <Users className="h-4 w-4 text-green-500" />;
      default:
        return <Calendar className="h-4 w-4 text-gray-500" />;
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case 'birthday':
        return 'text-pink-600 bg-pink-50';
      case 'anniversary':
        return 'text-blue-600 bg-blue-50';
      case 'joining':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatEventType = (eventType) => {
    return eventType.charAt(0).toUpperCase() + eventType.slice(1);
  };

  // Helpers and constants
  const VALID_EVENT_TYPES = useMemo(() => new Set(['birthday', 'anniversary']), []);
  const normalizeEventType = useCallback((value) => (value ?? '').toString().toLowerCase().trim(), []);

  // Filter only birthday and anniversary events
  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events.filter((evt) => VALID_EVENT_TYPES.has(normalizeEventType(evt?.eventType)));
  }, [events, VALID_EVENT_TYPES, normalizeEventType]);

  // Calculate pagination based on filtered events
  const totalPages = useMemo(() => Math.ceil(filteredEvents.length / eventsPerPage) || 1, [filteredEvents.length, eventsPerPage]);
  const { startIndex, endIndex, currentEvents } = useMemo(() => {
    const start = (currentPage - 1) * eventsPerPage;
    const end = start + eventsPerPage;
    return {
      startIndex: start,
      endIndex: end,
      currentEvents: filteredEvents.slice(start, end)
    };
  }, [currentPage, eventsPerPage, filteredEvents]);

  const handlePageChange = useCallback((page) => {
    if (page < 1) return;
    if (page > totalPages) return;
    setCurrentPage(page);
  }, [totalPages]);

  // Ensure current page remains valid when filtered list changes
  useEffect(() => {
    const newTotalPages = Math.ceil(filteredEvents.length / eventsPerPage) || 1;
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [filteredEvents.length, eventsPerPage, currentPage]);

  if (loading) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2 border-b border-gray-200 py-2 px-3">
          <Calendar className="h-5 w-5 text-blue-400" />
          <h4 className="text-md text-blue-400">Today's Events</h4>
        </div>
        <div className="p-3 text-center text-gray-500">
          Loading events...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col">
        {/* <div className="flex items-center gap-2 border-b border-gray-200 py-2 px-3">
          <Calendar className="h-5 w-5 text-red-400" />
          <h4 className="text-md text-red-400">Today's Events</h4>
        </div> */}
        <div className="p-3 text-center text-red-500">
          {error}
        </div>
      </div>
    );
  }

  if (filteredEvents.length === 0) {
    return (
      <div className="flex flex-col">
        {/* <div className="flex items-center gap-2 border-b border-gray-200 py-2 px-3">
          <Calendar className="h-5 w-5 text-gray-400" />
          <h4 className="text-md text-gray-400">Today's Events</h4>
        </div> */}
        <div className="p-3 text-center text-gray-500">
          No events today
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* <div className="flex items-center gap-2 border-b border-gray-200 py-2 px-3">
        <Calendar className="h-5 w-5 text-blue-400" />
        <h4 className="text-md text-blue-400 pt-2">Today's Events</h4>
        <span className="text-sm text-gray-500">({filteredEvents.length})</span>
      </div> */}
      
      <div className="max-h-64 overflow-y-auto">
        {currentEvents.map((event, index) => (
          <div key={`${event.employeeId}-${event.eventType}-${index}`} 
               className="border-b border-gray-100 last:border-b-0 p-3 hover:bg-gray-50">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {getEventIcon(normalizeEventType(event.eventType))}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">
                    {event.firstName} {event.lastName}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({event.employee_code})
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEventTypeColor(normalizeEventType(event.eventType))}`}>
                    {formatEventType(normalizeEventType(event.eventType))}
                  </span>
                  {event.yearsOfService && (
                    <span className="text-xs text-gray-500">
                      {event.yearsOfService} year{event.yearsOfService > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {totalPages > 1 && (
        <div className="flex justify-center px-3 py-2 border-t border-gray-200">
          <Pagination
            current={currentPage}
            total={filteredEvents.length}
            pageSize={eventsPerPage}
            onChange={handlePageChange}
            size="small"
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
          />
        </div>
      )}
    </div>
  );
};

export default EmployeeEvents;
